import * as React from 'react';
import {
  Link,
  useNavigate,
  type LinkProps,
  type NavigateFunction,
  type NavigateOptions,
  type To,
} from 'react-router-dom';
import {
  canonicalProductPath,
  type ProductDeploymentProfile,
} from './productPath';

const ProductNavigationProfileContext = React.createContext<ProductDeploymentProfile>(
  __PINPOINT_DEPLOYMENT_PROFILE__,
);

export function ProductNavigationProvider({
  deploymentProfile,
  children,
}: {
  deploymentProfile: ProductDeploymentProfile;
  children: React.ReactNode;
}) {
  return (
    <ProductNavigationProfileContext.Provider value={deploymentProfile}>
      {children}
    </ProductNavigationProfileContext.Provider>
  );
}

export function useProductDeploymentProfile(): ProductDeploymentProfile {
  return React.useContext(ProductNavigationProfileContext);
}

export function useCanonicalProductPath() {
  const profile = useProductDeploymentProfile();
  return React.useCallback(
    (destination: string) => canonicalProductPath(destination, profile),
    [profile],
  );
}

function canonicalTo(to: To, profile: ProductDeploymentProfile): To {
  if (typeof to === 'string') return canonicalProductPath(to, profile);
  if (typeof to.pathname !== 'string') return to;
  return { ...to, pathname: canonicalProductPath(to.pathname, profile) };
}

export function ProductLink({ to, ...props }: LinkProps) {
  const profile = useProductDeploymentProfile();
  return <Link {...props} to={canonicalTo(to, profile)} />;
}

export function useProductNavigate(): NavigateFunction {
  const navigate = useNavigate();
  const profile = useProductDeploymentProfile();
  return React.useCallback((to: To | number, options?: NavigateOptions) => {
    if (typeof to === 'number') {
      navigate(to);
      return;
    }
    const canonicalDestination = canonicalTo(to, profile);
    if (options === undefined) navigate(canonicalDestination);
    else navigate(canonicalDestination, options);
  }, [navigate, profile]) as NavigateFunction;
}
